import {
  ButtonComponent,
  FileAttachment,
  FormFields,
  FormStepComponent,
  WidgetComponent,
} from 'api/types';
import { PREFIX_TO_SKIP_RENDERING_WIDGET_COMPONENT } from 'components/App/consts';

type SuncoFormField = {
  label: string;
  maxSize: number;
  minSize: number;
  name: string;
  type: 'text';
};

export type SuncoMessage = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actions?: any[];
  altText?: string;
  avatarUrl: string;
  displayName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields?: any[];
  id: string;
  mediaSize?: number;
  mediaType?: string;
  mediaUrl?: string;
  metadata: Metadata;
  received: number;
  role: 'user' | 'business';
  source: {
    id: string;
    integrationId: string;
    type: 'web' | 'slack' | 'zd:surveys';
  };
  text: string;
  type: ContentType;
  userId: string;
};

export const processReceivedMessage = (
  message: SuncoMessage,
): WidgetComponent | null => {
  /*
   * convert the input sunco message to a list of widget components
   */
  if (message?.metadata?.type === 'csat' && message.actions) {
    return {
      component_fields: {
        agentIcon: message.avatarUrl,
        agentName: message.displayName,
        multi_select: false,
        options: message.actions.map(
          ({
            payload,
            text,
          }: {
            id: string;
            metadata: {
              account_id: number;
              score: 'GOOD' | 'BAD';
              ticket_id: number;
            };
            payload: string;
            text: string;
            type: 'reply';
          }) => ({
            display_name: text,
            value: payload,
          }),
        ),
        output_variable: '',
        prompt: message.text ?? '',
        should_render_as_markdown: true,
        should_render_single_new_line_characters: true,
      },
      component_type: 'buttons',
      isInAgentConvo: true,
      timestamp: message.received,
    } satisfies ButtonComponent;
  }

  // new type of CSAT
  if (
    message.source.type === 'zd:surveys' &&
    message.actions &&
    message.actions.length > 0
  ) {
    return {
      component_fields: {
        agentIcon: message.avatarUrl,
        agentName: message.displayName,
        csat_survey_link: message.actions[0].uri,
        csat_survey_text: message.actions[0].text,
        message: message.text ?? 'message',
      },
      component_type: 'text_message',
      isInAgentConvo: true,
      timestamp: message.received,
    };
  }

  if (message.type === 'text') {
    if (message.text.startsWith(PREFIX_TO_SKIP_RENDERING_WIDGET_COMPONENT)) {
      return null;
    }

    return {
      component_fields: {
        agentIcon: message.avatarUrl,
        agentName: message.displayName,
        message: message.text ?? 'message',
      },
      component_type: 'text_message',
      isInAgentConvo: true,
      timestamp: message.received,
    };
  }

  if (message.type === 'image' || message.type === 'file') {
    const attachments: FileAttachment[] = [
      {
        content_type: message.mediaType ?? '',
        filesize: message.mediaSize ?? 0,
        name: message.altText ?? '',
        url: message.mediaUrl ?? '',
      },
    ];
    return {
      component_fields: {
        agentIcon: message.avatarUrl,
        agentName: message.displayName,
        attachments: attachments,
        message: '',
      },
      component_type: 'text_message',
      isInAgentConvo: true,
      timestamp: message.received,
    };
  }

  if (
    message.type === 'form' &&
    // Only text fields are supported for now:
    message.fields?.every((field: SuncoFormField) => field.type === 'text')
  ) {
    return {
      component_fields: {
        agentIcon: message.avatarUrl,
        agentName: message.displayName,
        forethought_text: {
          checkbox_no: 'No',
          checkbox_placeholder: 'Select one',
          checkbox_yes: 'Yes',
          dropdown_back: 'Back',
          email_error: 'Please enter a valid email',
          never_mind: 'Never mind',
          number_error: 'Please enter a valid number',
          phone_error: 'Please enter a valid phone number',
          required_error: 'Field is required',
          required_label: 'Required',
          short_text_alphanumeric_error: 'Text must be alphanumeric',
          short_text_equal_error: 'Text must be exactly %s characters',
          short_text_max_error: 'Text cannot go over %s characters',
          short_text_min_error: 'Text needs to be at least %s characters',
          submit_button: 'Send',
        },
        form_fields: message.fields.map(
          ({ label, maxSize, minSize, name }: SuncoFormField) =>
            ({
              configuration_fields: {
                max_length: maxSize,
                min_length: minSize,
              },
              input_type: 'SHORT_TEXT',
              output_variable: name,
              placeholder: label,
            }) as const satisfies FormFields,
        ),
        prompt: message.text ?? '',
        quotedMessageId: message.id,
        should_render_as_markdown: true,
        should_render_single_new_line_characters: true,
      },
      component_type: 'form',
      isInAgentConvo: true,
      timestamp: message.received,
    } satisfies FormStepComponent;
  }

  return null;
};
